import React, { useCallback, useMemo, useRef, useState } from "react";
import TableContainer from "components/common/TableContainer";
import { ColumnDef } from "@tanstack/react-table";
import { CodeCell, IndexColumn } from "components/common/columns";
import { DateOnly, DateTime } from "components/common/Date";
import FinalizePayrollRunConfirmModal from "features/Payroll/components/FinalizePayrollRunConfirmModal";
import CancelPayrollRunConfirmModal from "features/Payroll/components/CancelPayrollRunConfirmModal";
import DeletePayrollRunConfirmModal from "features/Payroll/components/DeletePayrollRunConfirmModal";
import { getPayrollRuns } from "../services/PayrollService";
import type { PayrollRun } from "../types/payroll.types";
import StatusBadge from "components/common/StatusBadge";
import {
  canCancelPayrollRun,
  isPayrollRunDraft,
  PayrollRunStatus,
  payrollRunStatusMap,
} from "../constants/payroll.constants";
import { Link } from "react-router-dom";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  UncontrolledDropdown,
} from "reactstrap";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";

const MONTH_OPTIONS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - 2 + i);

const STATUS_OPTIONS = Object.entries(payrollRunStatusMap)
  .filter(([key]) =>
    Object.values(PayrollRunStatus).includes(
      key as (typeof PayrollRunStatus)[keyof typeof PayrollRunStatus],
    ),
  )
  .map(([value, { label }]) => ({ value, label }));

export const PayrollRunsTable = ({
  refreshRef,
}: {
  refreshRef?: React.RefObject<(() => void) | null>;
}) => {
  const { can } = useAccess();
  const canReadPayroll = can(Permissions.PAYROLL.READ);
  const canFinalizePayroll = can(Permissions.PAYROLL.FINALIZE);
  const canCancelPayroll = can(Permissions.PAYROLL.CANCEL);
  const canDeletePayroll = can(Permissions.PAYROLL.DELETE);
  const tableRef = useRef<(() => void) | null>(null);
  const refToUse = refreshRef ?? tableRef;
  const [finalizeRun, setFinalizeRun] = useState<PayrollRun | null>(null);
  const [cancelRun, setCancelRun] = useState<PayrollRun | null>(null);
  const [deleteRun, setDeleteRun] = useState<PayrollRun | null>(null);
  const [month, setMonth] = useState(0);
  const [year, setYear] = useState(0);
  const [status, setStatus] = useState("");

  const columns = useMemo<ColumnDef<PayrollRun>[]>(
    () => [
      IndexColumn<PayrollRun>(),
      {
        header: "Code",
        accessorKey: "code",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const run = row.original;
          const code = run.code ?? `#${run.id}`;
          return canReadPayroll ? (
            <Link
              to={`/finance/payroll/${run.id}`}
              className="text-decoration-none link"
            >
              <CodeCell code={code} />
            </Link>
          ) : (
            <CodeCell code={code} />
          );
        },
      },
      {
        header: "Period",
        accessorKey: "periodStart",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const r = row.original;
          return (
            <span className="text-body fw-medium">
              <DateOnly date={r.periodStart} /> —{" "}
              <DateOnly date={r.periodEnd} />
            </span>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        cell: ({ getValue }) => {
          const v = getValue() as string | undefined;
          if (!v) return <span className="text-muted">—</span>;
          return (
            <StatusBadge value={v} map={payrollRunStatusMap} />
          );
        },
      },
      {
        header: "Created",
        accessorKey: "created_at",
        enableColumnFilter: false,
        cell: ({ getValue }) => {
          const d = getValue() as string | undefined;
          return d ? (
            <DateTime date={d} />
          ) : (
            <span className="text-muted">—</span>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const run = row.original;
          if (!canReadPayroll) {
            return <span className="text-muted small">—</span>;
          }
          const showFinalize =
            canFinalizePayroll && isPayrollRunDraft(run.status);
          const showCancel =
            canCancelPayroll && canCancelPayrollRun(run.status);
          const showDelete =
            canDeletePayroll && isPayrollRunDraft(run.status);
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                type="button"
                className="btn btn-soft-secondary btn-sm"
                tag="button"
                aria-label="Payroll run actions"
              >
                <i className="ri-more-2-fill align-middle" />
              </DropdownToggle>
              <DropdownMenu container="body" end>
                <DropdownItem tag={Link} to={`/finance/payroll/${run.id}`}>
                  <i className="ri-eye-line align-bottom me-2 text-muted" />
                  View
                </DropdownItem>
                {showFinalize ? (
                  <DropdownItem
                    tag="button"
                    type="button"
                    onClick={() => setFinalizeRun(run)}
                  >
                    <i className="ri-check-double-line align-bottom me-2 text-muted" />
                    Finalize run
                  </DropdownItem>
                ) : null}
                {showCancel || showDelete ? <DropdownItem divider /> : null}
                {showCancel ? (
                  <DropdownItem
                    className="text-danger"
                    tag="button"
                    type="button"
                    onClick={() => setCancelRun(run)}
                  >
                    <i className="ri-close-circle-line align-bottom me-2" />
                    Cancel run
                  </DropdownItem>
                ) : null}
                {showDelete ? (
                  <DropdownItem
                    className="text-danger"
                    tag="button"
                    type="button"
                    onClick={() => setDeleteRun(run)}
                  >
                    <i className="ri-delete-bin-line align-bottom me-2" />
                    Delete run
                  </DropdownItem>
                ) : null}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [canCancelPayroll, canDeletePayroll, canFinalizePayroll, canReadPayroll],
  );

  const fetchApi = useCallback(
    async ({
      page,
      limit,
      search,
    }: {
      page: number;
      limit: number;
      search?: string;
    }) => {
      const res = await getPayrollRuns({
        page,
        limit,
        search,
        month: month || undefined,
        year: year || undefined,
        status: status || undefined,
      });
      return {
        success: res.success,
        message: res.message,
        data: res.data ?? {
          rows: [] as PayrollRun[],
          totalItems: 0,
          currentPage: page,
          pageSize: limit,
          limit,
          totalPages: 0,
        },
      };
    },
    [month, year, status],
  );

  return (
    <React.Fragment>
      <FinalizePayrollRunConfirmModal
        run={finalizeRun}
        isOpen={finalizeRun != null}
        onClose={() => setFinalizeRun(null)}
        onFinalized={() => refToUse.current?.()}
      />
      <CancelPayrollRunConfirmModal
        run={cancelRun}
        isOpen={cancelRun != null}
        onClose={() => setCancelRun(null)}
        onCancelled={() => refToUse.current?.()}
      />
      <DeletePayrollRunConfirmModal
        run={deleteRun}
        isOpen={deleteRun != null}
        onClose={() => setDeleteRun(null)}
        onDeleted={() => { setDeleteRun(null); refToUse.current?.(); }}
      />
      <TableContainer<PayrollRun>
        columns={columns}
        refreshRef={refToUse}
        pageSize={10}
        globalSearch={true}
        SearchPlaceholder="Search by code..."
        fetchApi={fetchApi}
        divClass="table-responsive table-card mb-3"
        tableClass="align-middle table-nowrap mb-0"
        theadClass="table-light text-muted"
        toolbarRight={
          <div className="d-flex gap-2 flex-wrap">
            <Input
              type="select"
              value={month}
              onChange={(e) => {
                const m = Number(e.target.value);
                setMonth(m);
                if (m && !year) setYear(CURRENT_YEAR);
              }}
              style={{ width: 130 }}
            >
              <option value={0}>All months</option>
              {MONTH_OPTIONS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </Input>
            <Input
              type="select"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              style={{ width: 100 }}
            >
              <option value={0}>All years</option>
              {YEAR_OPTIONS.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </Input>
            <Input
              type="select"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              style={{ width: 130 }}
            >
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </Input>
          </div>
        }
      />
    </React.Fragment>
  );
};
